import Loader from "components/Loader/loader"
import React, { useEffect, useRef, useState } from "react"
import { MdAdd, MdCancel, MdDownload, MdEdit } from "react-icons/md"
import { useParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import Swal from "sweetalert2"
import { fetchData, FetchPutData } from "utils/Common_api_functions"
import logo from "../../assets/images/logo.jpg"
import Signature from "../../assets/images/Signature.png"
import html2pdf from "html2pdf.js"
import SignatureCanvas from "react-signature-canvas"
import Configs from "../../config"

const ClientContract = () => {
  const { id } = useParams()
  const printref = useRef(null)
  const sigPadRef = useRef(null)

  const [loading, setLoading] = useState(false)
  const [contractDetails, setContractDetails] = useState({})

  const [signatureModal, setSignatureModal] = useState(false)
  const [signature, setSignature] = useState(null)
  const [signaturePreview, setSignaturePreview] = useState("")
  const [signMode, setSignMode] = useState("draw") // draw | upload

  /* ================= FETCH ================= */
  useEffect(() => {
    getContractDetails()
  }, [id])

  async function getContractDetails() {
    const APIdata = await fetchData(setLoading, `location/by/${id}`)
    if (APIdata?.success) {
      setContractDetails(APIdata?.data)
    } else {
      Swal.fire("Error", "Unable to fetch contract", "error")
    }
  }

  /* ================= SIGNATURE UPLOAD ================= */
  const handleSignatureChange = e => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      Swal.fire("Invalid file", "Upload image only", "error")
      return
    }

    setSignature(file)
    setSignaturePreview(URL.createObjectURL(file))
  }

  /* ================= DRAW SIGNATURE ================= */
  const saveDrawnSignature = () => {
    if (sigPadRef.current.isEmpty()) {
      Swal.fire("Please draw your signature", "", "info")
      return
    }

    const dataUrl = sigPadRef.current.getTrimmedCanvas().toDataURL("image/png")

    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "signature.png", {
          type: "image/png",
        })
        setSignature(file)
        setSignaturePreview(dataUrl)
      })
  }

  const clearDrawSignature = () => {
    sigPadRef.current?.clear()
    setSignature(null)
    setSignaturePreview("")
  }

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!signature) {
      Swal.fire("Please add a signature first")
      return
    }

    const APIdata = await FetchPutData(
      setLoading,
      "location/add/signature",
      id,
      { signature }
    )

    if (APIdata?.data?.success) {
      Swal.fire("Success", "Contract signed successfully", "success")
      getContractDetails()
      setSignatureModal(false)
    } else {
      Swal.fire("Error", "Failed to save signature", "error")
    }
  }

  /* ================= PDF ================= */
  const handleDownload = () => {
    html2pdf()
      .set({
        filename: `${contractDetails?.name || "contract"}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4" },
      })
      .from(printref.current)
      .save()
  }

  return (
    <div>
      {loading && <Loader />}

      <Container fluid className="pt-3">
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between mb-4">
              <h3>{contractDetails?.name}'s Contract</h3>
              <div className="d-flex gap-2">
                <Button color="primary" onClick={handleDownload}>
                  <MdDownload /> Download
                </Button>
                {!contractDetails?.signature && (
                  <Button
                    color="primary"
                    onClick={() => setSignatureModal(true)}
                  >
                    <MdAdd /> Signature
                  </Button>
                )}
              </div>
            </div>

            {/* CONTRACT */}
            <div ref={printref} className="px-3">
              <div className="text-center mb-4">
                <img src={logo} width={200} />
              </div>

              <div
                dangerouslySetInnerHTML={{
                  __html: contractDetails?.contract,
                }}
              />

              <div className="d-flex justify-content-between mt-5">
                <div>
                  <img src={Signature} width={150} height={80} />
                  <h6 className="mt-2 fw-bold">Joseph Kyle</h6>
                  <small>Director</small>
                </div>

                <div>
                  <img
                    src={
                      contractDetails?.signature
                        ? `${Configs.imageUrl}${contractDetails.signature}`
                        : signaturePreview
                    }
                    width={150}
                    height={80}
                  />
                  <h6 className="mt-2 fw-bold">
                    {contractDetails?.contactPerson}
                  </h6>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* ================= SIGNATURE MODAL ================= */}
      <Modal isOpen={signatureModal} centered size="lg">
        <ModalHeader toggle={() => setSignatureModal(false)}>
          Add Signature
        </ModalHeader>

        <ModalBody>
          {/* MODE SWITCH */}
          <div className="d-flex justify-content-center gap-3 mb-3">
            <Button
              outline={signMode !== "draw"}
              color="primary"
              onClick={() => setSignMode("draw")}
            >
              Signature
            </Button>
            <Button
              outline={signMode !== "upload"}
              color="primary"
              onClick={() => setSignMode("upload")}
            >
              Attach Signature
            </Button>
          </div>

          {/* DRAW */}
          {signMode === "draw" && (
            <>
              <SignatureCanvas
                ref={sigPadRef}
                penColor="black"
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: "border rounded w-100 bg-white",
                }}
              />

              <div className="d-flex justify-content-between mt-3">
                <Button color="danger" outline onClick={clearDrawSignature}>
                  Clear
                </Button>
                <Button color="primary" onClick={saveDrawnSignature}>
                  Use Signature
                </Button>
              </div>
            </>
          )}

          {/* UPLOAD */}
          {signMode === "upload" && (
            <>
              <div className="signature-upload">
                {" "}
                <label
                  htmlFor="signature-input"
                  className="signature-box"
                  style={{
                    border: "2px dashed #cbd5e0",
                    borderRadius: "8px",
                    padding: "2rem",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "block",
                    textAlign: "center",
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.borderColor = "#4299e1")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.borderColor = "#cbd5e0")
                  }
                >
                  {" "}
                  {signaturePreview ? (
                    <div style={{ position: "relative" }}>
                      {" "}
                      <img
                        src={signaturePreview}
                        alt="Signature Preview"
                        className="signature-preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "200px",
                          objectFit: "contain",
                        }}
                      />{" "}
                    </div>
                  ) : (
                    <div className="signature-placeholder">
                      {" "}
                      <MdEdit size={48} color="#4299e1" />{" "}
                      <p
                        className="mb-1 mt-3"
                        style={{ fontSize: "16px", fontWeight: "500" }}
                      >
                        {" "}
                        Click to upload signature{" "}
                      </p>{" "}
                      <small style={{ color: "#718096" }}>
                        {" "}
                        Supported formats: PNG, JPG, JPEG (Max 5MB){" "}
                      </small>{" "}
                    </div>
                  )}{" "}
                </label>{" "}
                <Input
                  id="signature-input"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  className="d-none"
                  onChange={handleSignatureChange}
                />{" "}
                {signaturePreview && (
                  <div className="d-flex gap-2 justify-content-center mt-3">
                    {" "}
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => {
                        setSignature(null)
                        setSignaturePreview("")
                      }}
                    >
                      {" "}
                      Remove{" "}
                    </Button>{" "}
                  </div>
                )}{" "}
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="primary" disabled={!signature} onClick={handleSave}>
            <MdEdit /> Submit
          </Button>
          <Button color="secondary" onClick={() => setSignatureModal(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ClientContract
